/**
 * Room read-state storage — web implementation.
 *
 * Persists the per-room `lastReadAt` map (#1180/#1611 lantern/unread-divider
 * state) behind a platform seam. Metro auto-prefers `roomReadState.native.ts`
 * when bundling the Expo app (same mechanism as `apiTransport.native.ts`), so
 * the shared `useRoomActivity` hook stays platform-agnostic.
 *
 * Web is synchronous (localStorage): `loadRoomReadStateSync()` returns the
 * persisted state immediately, so the hook hydrates in its `useState`
 * initializer exactly as it did before the seam existed — no behavior change
 * for the browser app.
 *
 * SEAM CONTRACT (both implementations must uphold):
 * - `loadRoomReadState()` NEVER rejects — every failure degrades to `{}`.
 *   `useRoomActivity`'s hydration relies on this (a rejection would freeze
 *   its `isHydrated` gate); it also guards with a defensive `.catch`.
 * - `saveRoomReadState()` never throws; failures degrade silently.
 * - Loaded state is shape-sanitized: only finite-number values survive, so
 *   corrupted-but-valid JSON can't leak NaN into timestamp comparisons.
 */

/** Per-room last-read timestamps (Unix seconds). */
export interface RoomViewState {
  [roomId: string]: number;
}

const STORAGE_KEY = 'oro_room_last_viewed';

/** Keep only { string: finite number } entries from untrusted parsed JSON. */
function sanitizeRoomViewState(parsed: unknown): RoomViewState {
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return {};
  }
  const out: RoomViewState = {};
  for (const [roomId, ts] of Object.entries(parsed)) {
    if (typeof ts === 'number' && Number.isFinite(ts)) {
      out[roomId] = ts;
    }
  }
  return out;
}

/**
 * Synchronously load persisted read-state.
 *
 * Returns the state object on platforms with synchronous storage (web).
 * Returns `null` on platforms that can only hydrate asynchronously (native) —
 * callers must then await `load()` and gate unread-divider logic on hydration.
 */
export function loadRoomReadStateSync(): RoomViewState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return sanitizeRoomViewState(JSON.parse(stored));
    }
  } catch {
    // Invalid JSON or localStorage error — degrade to empty state
  }
  return {};
}

/**
 * Asynchronously load persisted read-state. On web this resolves immediately
 * with the same value as `loadRoomReadStateSync()`; it exists so the shared
 * hook can use one code path for async-only platforms. Never rejects (see
 * seam contract above).
 */
export function loadRoomReadState(): Promise<RoomViewState> {
  return Promise.resolve(loadRoomReadStateSync() ?? {});
}

/**
 * Persist the FULL read-state map. Callers own merge semantics (no-regress
 * per room) and must never call this with a partial map — the write replaces
 * the stored value wholesale.
 */
export function saveRoomReadState(state: RoomViewState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage full / disabled — graceful degradation
  }
}
